/* eslint-disable react/require-default-props, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any */
import React from 'react';
import { Box, Link as DSLink, Typography } from '@saviynt/design-system';

import { SodCustomDonutChart } from '../../../../shared/components';
import {
  CF_NEU_BLD,
  CF_SEC_MED,
  ENTITLEMENT_DETAILS_TABS,
  violationsEntitlement,
} from '../../../../shared/constants';
import { useInvertedLocalize, useNavigateTo } from '../../../../shared/hooks';
import { type UserViolationChartDetails } from '../EntitlementDetails/UsersTab/data-layer';
import {
  calculatePercentage,
  getAbbreviateNumber,
  getTitleCase,
  getUserViolationChartData,
  usersDonutChartColorRange,
} from '../EntitlementDetails/UsersTab/utils';

import './ViolationDonutChart.css';

interface ViolationDonutChartProps {
  item: UserViolationChartDetails | null;
  onViewViolations?: () => void;
}

/**
 * ViolationDonutChart component for displaying interactive violation data visualization.
 *
 * This component provides a comprehensive donut chart visualization for SOD violation data
 * in the AAG (Access Analytics and Governance) module. It displays violation statistics
 * with color-coded categories, status breakdowns, and navigation capabilities.
 *
 * Features:
 * - Renders interactive SodCustomDonutChart with SOD violations count data
 * - Displays detailed status breakdown with color-coded indicators
 * - Shows abbreviated numbers and percentage calculations
 * - Provides "View Violations" navigation to entitlement details violations tab
 * - Handles null/empty data gracefully with proper fallbacks
 * - Uses localized content and formatting throughout
 * - Navigates to current entitlement's violations tab using URL-based routing
 * - Works with both real API data and zero-count scenarios
 *
 * @component
 * @param {ViolationDonutChartProps} props - Component props
 * @param {UserViolationChartDetails | null} props.item - SOD violations count data
 * @returns {ReactElement | null} Donut chart with status breakdown and navigation
 */
function ViolationDonutChart({
  item,
  onViewViolations,
}: ViolationDonutChartProps) {
  const l = useInvertedLocalize();
  const { navigateToDetails } = useNavigateTo();

  if (!item) {
    return null;
  }

  const data = getUserViolationChartData(item, l);

  // Always show chart, even with zero data
  const chartData =
    data.length === 0
      ? [
          { category: l('Open'), value: 0 },
          { category: l('Mitigated'), value: 0 },
          { category: l('Closed'), value: 0 },
          { category: l('In Review'), value: 0 },
          { category: l('Remediated'), value: 0 },
        ]
      : data;

  const handleSourceChange = () => {
    if (onViewViolations) {
      onViewViolations();
    } else {
      // Navigate to entitlement details violations tab
      navigateToDetails({
        key: String(window.location.pathname.split('/').pop() ?? 'entitlement'),
        params: {
          type: violationsEntitlement,
          activeTab: ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
        },
      });
    }
  };

  return (
    <Box className='Sod-Violations-Donut-container'>
      <Box className='Sod-Violations-Donut-chart-wrapper'>
        <SodCustomDonutChart
          data={chartData}
          colorRange={usersDonutChartColorRange}
          value={item.violationCount ?? 0}
          label={l('Violations')}
          width={140}
          height={140}
          innerRadius={60}
        />
        <Box className='Sod-Violations-Donut-chart-status-container'>
          {chartData.map((detail, index) => {
            const percentage = Math.round(
              parseFloat(
                calculatePercentage(detail.value, item.violationCount ?? 0, 3)
              )
            );
            const percentageText = `(${percentage}%)`;

            return (
              <Box
                className='Sod-Violations-Donut-chart-status-row'
                key={detail.category}>
                <Box
                  className='Sod-Violations-Donut-chart-status-row-info'
                  style={{
                    borderLeftColor: usersDonutChartColorRange[index],
                  }}>
                  <Typography kind='body1' color={CF_NEU_BLD}>
                    {getTitleCase(detail.category)}
                  </Typography>
                  <Typography
                    className='Sod-Violations-Donut-chart-status-row-info-text'
                    kind='h2'
                    color={CF_NEU_BLD}>
                    {getAbbreviateNumber(detail.value)}
                  </Typography>
                </Box>
                <Typography
                  className='Sod-Violations-Donut-chart-status-row-percentage'
                  kind='body1'
                  color={CF_SEC_MED}>
                  {percentageText}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      {(onViewViolations || (item.violationCount ?? 0) > 0) && (
        <DSLink
          kind='button'
          onClick={handleSourceChange}
          text={l('View Violations')}
          size='medium'
          color='brand'
          suffixIconKind='ArrowRight'
          className='Sod-Violations-Donut-chart-viewViolations'
        />
      )}
    </Box>
  );
}

export default ViolationDonutChart;
